/**
 * Case owner, Hand off and logged off-channel contact (issue #201). The owner is the primary Desk ticket's assignee:
 * set under your own Desk token and read back by the server; Desk-side changes flow back through the ingest. Every
 * read is a GET (no Desk call); every write is a POST with the CSRF header. Owner: feat/case-owner-handoff.
 */
import { apiRequest } from '../board/api'

export type OffChannel = 'softphone' | 'phone' | 'teams' | 'sms'

export const CHANNELS: { value: OffChannel; label: string }[] = [
  { value: 'softphone', label: 'Softphone' },
  { value: 'phone', label: 'Phone' },
  { value: 'teams', label: 'Teams' },
  { value: 'sms', label: 'SMS' },
]

export interface DeskOwnerDto {
  desk_ticket_id: string
  desk_agent_id?: string | null
  name?: string | null
  user_id?: string | null
}

export interface OwnerPersonDto {
  desk_agent_id: string
  zuid?: string | null
  name: string
  email?: string | null
  user_id?: string | null
}

export interface OwnersDto {
  people: OwnerPersonDto[]
  desk_directory: 'ok' | 'unreachable'
}

export interface HandoffBody {
  desk_agent_id: string
  channel: OffChannel
  passed_on: string
  next_move_text: string
  due_at: string
  idempotency_key: string
  suggestion_id?: string
}

export type OwnerEvidenceSource = 'category' | 'customer' | 'workload' | 'on_call'

export interface OwnerHandoffEvidenceDto {
  source: OwnerEvidenceSource
  detail: string
  matched_cases?: number | null
  open_cases?: number | null
  weekly_capacity_hours?: number | null
}

export interface OwnerHandoffSuggestionDto {
  case_id: string
  suggestion_id?: string | null
  owner?: OwnerPersonDto | null
  category?: string | null
  evidence: OwnerHandoffEvidenceDto[]
  why: string
  recent_human_assignment: boolean
}

export interface HandoffDto {
  owner: DeskOwnerDto
  owner_changed: boolean
  next_move: { id: string; text: string; due_at?: string | null }
  note: { posted: boolean; code?: string | null; detail?: string | null; mentions: { zuid: string; name: string; notified: boolean }[] }
  reconciled: boolean
}

export interface OffChannelBody {
  channel: OffChannel
  with_whom: string
  summary: string
  idempotency_key: string
}

export const ownerKeys = {
  case: (caseId: string) => ['case-owner', caseId] as const,
  suggestion: (caseId: string) => ['owner-handoff-suggestion', caseId] as const,
  people: ['case-owner', 'people'] as const,
}

const caseBase = (caseId: string) => `/api/v1/cases/${encodeURIComponent(caseId)}`

export async function caseOwner(caseId: string): Promise<DeskOwnerDto | null> {
  const out = await apiRequest<{ owner?: DeskOwnerDto | null }>(`${caseBase(caseId)}/owner`)
  if (!out || typeof out !== 'object' || !('owner' in out)) throw new Error('The owner answered in an unexpected shape')
  return out.owner ?? null
}

export function owners(): Promise<OwnersDto> {
  return apiRequest<OwnersDto>('/api/v1/owners')
}

export function setOwner(caseId: string, deskAgentId: string): Promise<{ owner: DeskOwnerDto; changed: boolean }> {
  return apiRequest(`${caseBase(caseId)}/owner`, { method: 'POST', body: JSON.stringify({ desk_agent_id: deskAgentId }) })
}

export function handOff(caseId: string, body: HandoffBody): Promise<HandoffDto> {
  return apiRequest<HandoffDto>(`${caseBase(caseId)}/handoff`, { method: 'POST', body: JSON.stringify(body) })
}

export function ownerHandoffSuggestion(caseId: string): Promise<OwnerHandoffSuggestionDto> {
  return apiRequest<OwnerHandoffSuggestionDto>(`${caseBase(caseId)}/owner-handoff/suggestion`)
}

export function rejectOwnerHandoff(caseId: string, suggestionId: string, reason?: string): Promise<{ recorded: boolean }> {
  return apiRequest(`${caseBase(caseId)}/owner-handoff/reject`, {
    method: 'POST',
    body: JSON.stringify({ suggestion_id: suggestionId, reason }),
  })
}

export function logOffChannel(caseId: string, body: OffChannelBody): Promise<{ desk_comment_id: string }> {
  return apiRequest(`${caseBase(caseId)}/off-channel`, { method: 'POST', body: JSON.stringify(body) })
}

export function newKey(): string {
  return globalThis.crypto.randomUUID()
}
